import * as React from 'react';
import {Link} from "react-router-dom";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {
    ICustomerPortalService,
    Policy,
    Types,
    ValuableItemCoverLocation,
    ValuableItemTypeEnum,
    InsuredValuableItem,
    resourcesHelper,
    HomeCoverTier,
    CLAIM_TYPE_OTHER,
    POLICY_STATUS_MTC,
    PREMIUM_TYPE_ANNUAL,
} from "@falcon/services";
import {formatDate, getAddress, getAmountToDisplay, getLink, setSessionStorage,} from "../../../../../CommonFunctions";
import {lazyInject} from "@athena-portal/core";
import {Overlay} from "@athena-portal/components";
import {
    convertDateTime,
    renderCoverStartDate,
    Detail,
    DetailKey,
    DetailValue,
    SectionContents,
    PolicySummaryPriceWidget
} from "@falcon/plugin-falcon-journey";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {ListItem} from "@athena-portal/journey-model";
import {PanelDividerLine} from "../../../../../components/PanelDividerLine";
import * as moment from "moment";

export interface IProps {
    policyNumber: string;
    policy: Policy;
    editAddress?: boolean;
}

export interface IState {
    maritalStatus?: string
    employmentStatus?: string
    jpEmploymentStatus?: string
    occupation?: string
    jpOccupation?: string
    occupationIndustry?: string
    jpOccupationIndustry?: string
    secondaryOccupation?: string
    jpSecondaryOccupation?: string
    secondaryOccupationIndustry?: string
    jpSecondaryOccupationIndustry?: string
    claimType?: string
    documentVersion?: string;
}

export class HomePolicySummary extends React.Component<IProps, IState> {
    protected pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    protected customerPortalService: ICustomerPortalService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    protected lookupService: ILookupService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            maritalStatus: "",
            employmentStatus: "",
            jpEmploymentStatus: "",
            occupation: "",
            jpOccupation: "",
            occupationIndustry: "",
            jpOccupationIndustry: "",
            secondaryOccupation: "",
            jpSecondaryOccupation: "",
            secondaryOccupationIndustry: "",
            jpSecondaryOccupationIndustry: "",
            claimType: "",
        }

    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy summary");
        this.fetchLookupValues(this.props.policy);
    }

    public render(): React.ReactNode {
        if (!this.props.policy?.interaction?.annualHomeFixedAddons?.homeEmergency) {
            setSessionStorage("claim", CLAIM_TYPE_OTHER);
        }

        if (!this.props.policy) {
            return <Overlay show={true}/>
        } else {
            return (
                <div className="section">
                    <SectionContents className="cover-details">
                        {this.renderCoverDetails()}
                        {this.renderSpecifiedItems()}
                        {this.renderPersonalBelongings()}
                        {this.renderBicycles()}
                        {this.renderFixedAddons()}
                    </SectionContents>
                    <PanelDividerLine/>
                    {this.renderExcesses()}
                    <PanelDividerLine/>
                    {this.renderPolicyHolderDetails()}
                    <PanelDividerLine/>
                    {this.props.policy.interaction.jointPolicyHolder && this.renderJointPolicyHolderDetails(this.props.policy.interaction.jointPolicyHolder)}
                    {this.props.policy.interaction.jointPolicyHolder && <PanelDividerLine/>}
                </div>
            )
        }
    }

    public renderPolicyNumber(): JSX.Element {
        return (
            <div className="page-heading-white">
                <span className="font-weight-light">Policy no: </span>
                {this.props.policyNumber}
            </div>
        );
    }

    protected renderCoverDetails(): JSX.Element {
        const premiumType: string = this.props.policy?.interaction.factors.premiumType;
        const price: number = premiumType === PREMIUM_TYPE_ANNUAL ? this.props.policy?.interaction.factors.annualPremium : this.props.policy?.interaction.paymentDetails.instalmentPlan.monthlyPayment;
        const totalPaid: number = this.props.policy?.interaction.paymentDetails.instalmentPlan?.totalAmount;
        return (
            <React.Fragment>
                <PolicySummaryPriceWidget
                    price={price}
                    premiumType={premiumType}
                    totalPaid={totalPaid}/>
                <h2 className="text-center mb-3 section-heading">Policy details</h2>
                <Detail className="cover-start-date">
                    <DetailKey>
                        Cover start date:
                    </DetailKey>
                    <DetailValue>
                        {renderCoverStartDate(this.props.policy.startDate)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Cover expiry date:
                    </DetailKey>
                    <DetailValue>
                        {convertDateTime(this.props.policy.endDate)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Cover type:
                    </DetailKey>
                    <DetailValue>
                        Buildings and contents:
                    </DetailValue>
                </Detail>
                <Detail className="address">
                    <DetailKey>
                        Insured address:
                    </DetailKey>
                    <DetailValue>
                        {getAddress(this.props.policy?.insuredRealEstates[0].address)} {this.props.policy?.insuredRealEstates[0].address.postalCode}
                    </DetailValue>
                    {this.props.editAddress && this.props.policy?.status !== POLICY_STATUS_MTC && <div className="text-center">
                        <div className="detail-key">
                            <Link
                                to={getLink('/journey/show/product/AnnualHomeInsurance/process/mtaChangeAddress/preset/' + this.props.policy?.interaction._id, false)}
                                className={`text-decoration-underline cursor-pointer text-blue-1 muli-bold-16`}
                                id="editAddress"
                            >
                                Edit Address
                            </Link>
                        </div>
                    </div>}
                </Detail>

                <div className="detail premium-tax">
                    <div className="detail-value">
                        {premiumType === PREMIUM_TYPE_ANNUAL ?
                            'incl. Insurance Premium Tax' :
                            'incl. Interest and Insurance Premium Tax'
                        }</div>
                </div>
            </React.Fragment>
        );
    }

    protected renderSpecifiedItems(): JSX.Element {
        const specifiedItems: InsuredValuableItem[] | undefined = this.props.policy?.insuredValuableItems
            .filter(bicycle => bicycle.valuableItemType.code !== ValuableItemTypeEnum.BIKE);

        const renderedSpecifiedItems: any = [];

        if (Array.isArray(specifiedItems) && specifiedItems.length !== 0) {
            specifiedItems.forEach(item => {
                renderedSpecifiedItems.push(
                    <span><b>{item.description}</b> covered {item.coverLocation === ValuableItemCoverLocation.ANYWHERE ? "in and out of your home" : "in your home"} for £{item.value}<br/></span>
                    );
                }
            )
        } else {
            renderedSpecifiedItems.push(<span>None<br/></span>);
        }
        return (
            <Detail>
                <DetailKey>
                    Specified items:
                </DetailKey>
                <DetailValue>
                    {renderedSpecifiedItems}
                </DetailValue>
            </Detail>
        )
    }

    protected renderPersonalBelongings(): JSX.Element {

        return (
            <Detail>
                <DetailKey>
                    Personal Belongings:
                </DetailKey>
                <DetailValue>
                    {this.props.policy?.interaction.annualHomeExtraCovers.personalPossessionsCover && this.props.policy?.interaction.annualHomeExtraCovers.personalPossessionCoverLimit ?
                        "Up to " + getAmountToDisplay(this.props.policy?.interaction.annualHomeExtraCovers.personalPossessionCoverLimit) : "No cover"}
                </DetailValue>
            </Detail>
        )
    }

    protected renderBicycles(): JSX.Element {
        const bicycles: InsuredValuableItem[] | undefined = this.props.policy?.insuredValuableItems
            .filter(bicycle => bicycle.valuableItemType.code === ValuableItemTypeEnum.BIKE);

        const renderedBicycles: any = [];

        if (Array.isArray(bicycles) && bicycles.length !== 0) {
            bicycles.forEach(item => {
                    renderedBicycles.push(
                        <span><b>{item.description}</b> covered {item.coverLocation === ValuableItemCoverLocation.ANYWHERE ? "in and out of your home" : "in your home"} for £{item.value}<br/></span>
                    );
                }
            )
        } else {
            renderedBicycles.push(<span>None<br/></span>);
        }
        return (
            <Detail>
                <DetailKey>
                    Bicycles:
                </DetailKey>
                <DetailValue>
                    {renderedBicycles}
                </DetailValue>
            </Detail>
        )
    }

    protected renderFixedAddons(): JSX.Element {

        return (
            <React.Fragment>
                <Detail>
                    <DetailKey>
                        Buildings accidental damage:
                    </DetailKey>
                    <DetailValue>
                        {this.getCoverStatus(this.props.policy?.interaction.annualHomeFixedAddons.buildingAccidentalDamage)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Contents accidental damage:
                    </DetailKey>
                    <DetailValue>
                        {this.getCoverStatus(this.props.policy?.interaction.annualHomeFixedAddons.contentsAccidentalDamage)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Home Legal Expenses:
                    </DetailKey>
                    <DetailValue>
                        {this.getCoverStatus(this.props.policy?.interaction.annualHomeFixedAddons.homeLegalExpenses)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Home Emergency:
                    </DetailKey>
                    <DetailValue>
                        {this.getCoverStatus(this.props.policy?.interaction.annualHomeFixedAddons.homeEmergency)}
                    </DetailValue>
                </Detail>
            </React.Fragment>
        )
    }

    protected renderExcesses(): JSX.Element {
        const subsidenceLandslipOrHeave: number = this.props.policy?.interaction.annualHomeExcesses.subsidence;
        const escapeOfWater: number = this.props.policy?.interaction.annualHomeExcesses.escapeOfWater;
        const flood: number = this.props.policy?.interaction.annualHomeExcesses.flood;
        const allOtherClaims: number = this.props.policy?.interaction.annualHomeExcesses.allOtherClaims;
        return (
            <React.Fragment>
                <h2 className="text-center section-heading margin-bottom-0 margin-top-32 icon-money-banknote-before">Excesses</h2>
                <SectionContents>
                    <Detail>
                        <DetailKey>
                            Subsidence, landslip or heave:
                        </DetailKey>
                        <DetailValue>
                            {getAmountToDisplay(subsidenceLandslipOrHeave)}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Escape of water:
                        </DetailKey>
                        <DetailValue>
                            {getAmountToDisplay(escapeOfWater)}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Flood:
                        </DetailKey>
                        <DetailValue>
                            {getAmountToDisplay(flood)}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            All other claims:
                        </DetailKey>
                        <DetailValue>
                            {getAmountToDisplay(allOtherClaims)}
                        </DetailValue>
                    </Detail>
                </SectionContents>
            </React.Fragment>
        );
    }

    protected renderPolicyHolderDetails() {
        return (
            <React.Fragment>
                <img className="home-account-details-image mb-2 margin-bottom-0"
                     src={`/${resourcesHelper.THEME}/img/account-details.png`}
                     alt="Account details icon"/>
                <h2 className="text-center section-heading">Policyholder details</h2>
                <SectionContents>
                    <Detail>
                        <DetailKey className="policyholder-name">
                            {this.props.policy?.policyHolder.firstName} {this.props.policy?.policyHolder.lastName}
                        </DetailKey>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Date of birth:
                        </DetailKey>
                        <DetailValue>
                            {moment(this.props.policy?.policyHolder.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Email:
                        </DetailKey>
                        <DetailValue>
                            {this.props.policy?.policyHolder.email}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Phone number:
                        </DetailKey>
                        <DetailValue>
                            {this.props.policy?.policyHolder.phoneMobile}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Marital status:
                        </DetailKey>
                        <DetailValue>
                            {this.state.maritalStatus}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Employment status:
                        </DetailKey>
                        <DetailValue>
                            {this.state.employmentStatus}
                        </DetailValue>
                    </Detail>
                    {this.state.occupation!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Occupation:
                            </DetailKey>
                            <DetailValue>
                                {this.state.occupation}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.occupationIndustry!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Industry:
                            </DetailKey>
                            <DetailValue>
                                {this.state.occupationIndustry}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.secondaryOccupation!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Secondary occupation:
                            </DetailKey>
                            <DetailValue>
                                {this.state.secondaryOccupation}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.secondaryOccupationIndustry!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Secondary occupation industry:
                            </DetailKey>
                            <DetailValue>
                                {this.state.secondaryOccupationIndustry}
                            </DetailValue>
                        </Detail>
                    }
                    <Detail>
                        <DetailKey>
                            Claims in the last 5 years:
                        </DetailKey>
                        <DetailValue>
                            {this.renderClaimOrNone()}
                        </DetailValue>
                    </Detail>
                </SectionContents>
            </React.Fragment>);
    }

    protected renderJointPolicyHolderDetails(jointPolicyHolder: any) {
        return (
            <React.Fragment>
                <img className="home-account-details-image mb-2 home-icon"
                     src={`/${resourcesHelper.THEME}/img/account-details.png`}
                     alt="Account details icon"/>
                <h2 className="text-center section-heading">Joint policyholder</h2>
                <SectionContents>
                    <Detail>
                        <DetailKey>
                            {jointPolicyHolder.firstName} {jointPolicyHolder.lastName}
                        </DetailKey>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Date of birth
                        </DetailKey>
                        <DetailValue>
                            {moment(jointPolicyHolder.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}
                        </DetailValue>
                    </Detail>
                    <Detail>
                        <DetailKey>
                            Employment status
                        </DetailKey>
                        <DetailValue>
                            {this.state.jpEmploymentStatus}
                        </DetailValue>
                    </Detail>
                    {this.state.jpOccupation!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Occupation
                            </DetailKey>
                            <DetailValue>
                                {this.state.jpOccupation}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.jpOccupationIndustry!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Industry
                            </DetailKey>
                            <DetailValue>
                                {this.state.jpOccupationIndustry}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.jpSecondaryOccupation!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Secondary occupation
                            </DetailKey>
                            <DetailValue>
                                {this.state.jpSecondaryOccupation}
                            </DetailValue>
                        </Detail>
                    }
                    {this.state.jpSecondaryOccupationIndustry!.length > 1 &&
                        <Detail>
                            <DetailKey>
                                Secondary occupation industry
                            </DetailKey>
                            <DetailValue>
                                {this.state.jpSecondaryOccupationIndustry}
                            </DetailValue>
                        </Detail>
                    }
                </SectionContents>
            </React.Fragment>);
    }

    protected renderClaimOrNone(): JSX.Element {
        return (<>
            {this.props.policy?.interaction.policyHolder.hasClaims && this.props.policy?.interaction.policyHolder.numberOfClaims > 0 && this.props.policy?.interaction.policyHolder.claims?.length > 0 ?
                this.renderClaims(this.props.policy?.interaction.policyHolder.claims) : 'None'}
        </>);
    }

    protected renderClaims(claims: any): JSX.Element | JSX.Element[] {
        return claims.map((claim: any, index: any) =>
            <div key={claim.claimType + index}
                 className="detail-value">{this.resolveClaimType(claim.claimType)}, {formatDate(claim.date, "MMM YYYY")}</div>
        )
    }

    protected resolveClaimType(claimId: any): string {
        if (!sessionStorage.classifier_homeClaimType) {
            this.lookupService.fetchLookupValues("homeClaimType", "en-UK").subscribe(() => {
                this.setState({});
            });
            return "";
        } else {
            const claimTypeNamingList = JSON.parse(sessionStorage.classifier_homeClaimType);
            return claimTypeNamingList.find((item: any) => item.id === claimId).value;
        }
    }

    protected getCoverStatus(cover: boolean): string {
        if (this.props.policy?.interaction.coverTier === HomeCoverTier.GOLD) {
            return "Included";
        }
        if (cover) {
            return "Included";
        } else {
            return "Not included";
        }
    }

    protected fetchLookupValues = (p: Policy) => {
        const languageCode = "en-UK";

        const valuesToPopulate = [
            {
                classifier: "maritalStatus",
                field: p.interaction.policyHolder.maritalStatus,
                stateName: "maritalStatus"
            },
            {
                classifier: "employmentStatus",
                field: p.interaction.policyHolder.employmentStatus,
                stateName: "employmentStatus"
            },
            {
                classifier: "employmentStatus",
                field: p.interaction.jointPolicyHolder?.employmentStatus,
                stateName: "jpEmploymentStatus"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.policyHolder.occupation,
                stateName: "occupation"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.jointPolicyHolder?.occupation,
                stateName: "jpOccupation"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.policyHolder.occupationIndustry,
                stateName: "occupationIndustry"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.jointPolicyHolder?.occupationIndustry,
                stateName: "jpOccupationIndustry"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.policyHolder.secondaryOccupation,
                stateName: "secondaryOccupation"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.jointPolicyHolder?.secondaryOccupation,
                stateName: "jpSecondaryOccupation"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.policyHolder.secondaryOccupationIndustry,
                stateName: "secondaryOccupationIndustry"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.jointPolicyHolder?.secondaryOccupationIndustry,
                stateName: "jpSecondaryOccupationIndustry"
            },
            {
                classifier: "homeClaimType",
                field: null,
                stateName: ""
            }
        ];

        valuesToPopulate.forEach(item => {
            const lookupFieldValue = item.field ?? undefined;
            this.lookupService.fetchLookupValues(item.classifier, languageCode, lookupFieldValue).subscribe((lookupValues: ListItem[]) => {
                const fetchedLookupItem = lookupValues.find(lookupValue => lookupValue.id === item.field);
                if (fetchedLookupItem) {
                    this.setState({[item.stateName]: fetchedLookupItem.value});
                }
            });
        });

    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/home/policySummary/HomePolicySummary.tsx